import { useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { Link, useLocation } from 'react-router-dom';

import { COMPOSE_REPLY } from 'mastodon/actions/compose';
import { openModal } from 'mastodon/actions/modal';
import { fetchContext } from 'mastodon/actions/statuses_typed';
import { StatusQuoteManager } from 'mastodon/components/status_quoted';
import { StatusStatsBar } from 'mastodon/components/status_stats_bar';
import ComposeFormContainer from 'mastodon/features/compose/containers/compose_form_container';
import { getDescendantsIds } from 'mastodon/selectors/contexts';
import { useAppDispatch, useAppSelector } from 'mastodon/store';
import { unescapeHTML } from 'mastodon/utils/html';

export const MediaModalSidebar: React.FC<{
  statusId: string;
  onClose: () => void;
}> = ({ statusId, onClose }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const openedAtRef = useRef(`${location.pathname}${location.search}`);

  const status = useAppSelector((state) => state.statuses.get(statusId));
  const account = useAppSelector((state) =>
    status ? state.accounts.get(status.get('account') as string) : undefined,
  );
  const descendantsIds = useAppSelector((state) =>
    getDescendantsIds(state, statusId),
  );
  const isReplyingToThis = useAppSelector(
    (state) => state.compose.get('in_reply_to') === statusId,
  );

  const [composerOpen, setComposerOpen] = useState(false);

  useEffect(() => {
    void dispatch(fetchContext({ statusId }));
  }, [dispatch, statusId]);

  useEffect(() => {
    // The sidebar renders full statuses whose permalink / group / hashtag /
    // mention links navigate the page behind the lightbox. If the location
    // changes while the modal is open, close it so the destination isn't
    // hidden underneath.
    if (`${location.pathname}${location.search}` !== openedAtRef.current) {
      onClose();
    }
  }, [location.pathname, location.search, onClose]);

  const handleStartReply = useCallback(() => {
    if (!status || !account) {
      return;
    }

    dispatch((_, getState) => {
      const state = getState();
      const draftText = unescapeHTML(state.compose.get('text') as string);

      if (draftText.trim().length !== 0) {
        // The confirmation modal dispatches replyCompose() on confirm,
        // which flips isReplyingToThis and swaps in the composer.
        dispatch(
          openModal({ modalType: 'CONFIRM_REPLY', modalProps: { status } }),
        );
      } else {
        dispatch({
          type: COMPOSE_REPLY,
          status: status.set('account', account),
        });
        setComposerOpen(true);
      }
    });
  }, [dispatch, status, account]);

  if (!status) {
    return null;
  }

  return (
    <div className='media-modal__sidebar'>
      {account && (
        <div className='media-modal__sidebar__header'>
          <FormattedMessage
            id='media_modal.from_post'
            defaultMessage='This is from a post.'
          />{' '}
          <Link onClick={onClose} to={`/@${account.get('acct')}/${statusId}`}>
            <FormattedMessage
              id='media_modal.view_post'
              defaultMessage='View post'
            />
          </Link>
        </div>
      )}

      <StatusQuoteManager id={statusId} contextType='thread' hideMedia />

      <div className='detailed-status__meta media-modal__sidebar__stats'>
        <StatusStatsBar status={status} onNavigate={onClose} />
      </div>

      <div className='media-modal__sidebar__reply'>
        {composerOpen || isReplyingToThis ? (
          <ComposeFormContainer
            // This is fine on this single-purpose view
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        ) : (
          <button
            type='button'
            className='media-modal__sidebar__reply-placeholder'
            onClick={handleStartReply}
          >
            <FormattedMessage
              id='media_modal.write_comment'
              defaultMessage='Write a comment…'
            />
          </button>
        )}
      </div>

      {descendantsIds.map((id) => (
        <StatusQuoteManager key={id} id={id} contextType='thread' />
      ))}
    </div>
  );
};
